import React from 'react'
import {
  ShoppingCart,
  Building2,
  Truck,
  Calculator,
  FileText,
  Settings,
  Users,
  Store,
} from 'lucide-react'
import styled from 'styled-components'
import { colors } from '../styleGuide'

export interface ModuleConfig {
  icon: React.ComponentType<any>
  label: string
  position:
    | 'top'
    | 'top-right'
    | 'right'
    | 'bottom-right'
    | 'bottom'
    | 'bottom-left'
    | 'left'
    | 'top-left'
}

export interface BusinessDiagramProps {
  modules?: ModuleConfig[]
  centralText?: string
  size?: number
  className?: string
}

// Default modules for the business diagram
const defaultModules: ModuleConfig[] = [
  { icon: ShoppingCart, label: 'Orders', position: 'top-right' },
  {
    icon: Building2,
    label: 'Multi-warehouse\nInventory',
    position: 'right',
  },
  { icon: Truck, label: 'Logistics', position: 'bottom-right' },
  { icon: Calculator, label: 'Accounting', position: 'bottom' },
  { icon: FileText, label: 'Reporting', position: 'bottom-left' },
  { icon: Settings, label: 'Supply Chain', position: 'left' },
  { icon: Users, label: 'Customer Service', position: 'top-left' },
  { icon: Store, label: 'POS (Retail Store)', position: 'top' },
]

const DiagramContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
`

const BusinessDiagram = styled.div<{ size: number }>`
  position: relative;
  width: ${(props) => props.size}px;
  height: ${(props) => props.size}px;
`

const CentralCircle = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 90px;
  height: 90px;
  background: white;
  border-radius: 50%;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  border: 3px solid #3b82f6;
  z-index: 2;

  span {
    font-size: 1rem;
    font-weight: bold;
    background: ${colors.primaryGradient};
    -webkit-background-clip: text;
    background-clip: text;
    color: transparent;
  }
`

const ModuleIcon = styled.div<{ position: string }>`
  position: absolute;
  width: 80px;
  height: 80px;
  background: ${colors.primaryGradient2};
  border-radius: 50%;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: white;
  border: 2px solid white;
  z-index: 1;

  ${({ position }) => {
    switch (position) {
      case 'top':
        return `top: 0; left: 50%; transform: translate(-50%, -50%);`
      case 'top-right':
        return `top: 15%; right: 15%;`
      case 'right':
        return `right: 0; top: 50%; transform: translate(50%, -50%);`
      case 'bottom-right':
        return `bottom: 15%; right: 15%;`
      case 'bottom':
        return `bottom: 0; left: 50%; transform: translate(-50%, 50%);`
      case 'bottom-left':
        return `bottom: 15%; left: 15%;`
      case 'left':
        return `left: 0; top: 50%; transform: translate(-50%, -50%);`
      case 'top-left':
        return `top: 15%; left: 15%;`
      default:
        return ''
    }
  }}

  svg {
    width: 20px;
    height: 20px;
    margin-bottom: 2px;
  }

  span {
    font-size: 11px;
    text-align: center;
    font-weight: 500;
    line-height: 1.1;
    white-space: pre-line;
  }
`

export const PixelBusinessDiagram: React.FC<BusinessDiagramProps> = ({
  modules = defaultModules,
  centralText = 'PixelOne',
  size = 350,
  className,
}) => {
  return (
    <DiagramContainer className={className}>
      <BusinessDiagram size={size}>
        <CentralCircle>
          <span>{centralText}</span>
        </CentralCircle>

        {modules.map((module, index) => {
          const Icon = module.icon
          return (
            <ModuleIcon key={index} position={module.position}>
              <Icon />
              <span>{module.label}</span>
            </ModuleIcon>
          )
        })}
      </BusinessDiagram>
    </DiagramContainer>
  )
}

export default PixelBusinessDiagram
